import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

/**
 * Help text that appears on hover (no browser title delay) and toggles on click
 * (touch / persistent read). Rendered in a portal so overflow:hidden parents do not clip it.
 */
export default function ContextHelpTip({
  text,
  theme = 'light',
  iconClassName = 'h-4 w-4 shrink-0',
  className = '',
}) {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: false });

  const visible = hovering || pinned;

  const updatePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;

    // Measure the actual panel when it's mounted; fall back to estimates otherwise.
    const panel = panelRef.current;
    const panelWidth = panel?.offsetWidth || 288;
    const panelHeight = panel?.offsetHeight || 120;

    // Vertical: prefer below; flip above only if it would overflow and there is room.
    const belowTop = r.bottom + margin;
    const placeAbove =
      belowTop + panelHeight > window.innerHeight - margin && r.top > panelHeight + margin;
    let top = placeAbove ? r.top - margin : belowTop;
    // Safety clamp so the panel never runs past the top/bottom edges.
    const minTop = placeAbove ? margin + panelHeight : margin;
    const maxTop = placeAbove ? window.innerHeight - margin : window.innerHeight - margin - panelHeight;
    if (minTop <= maxTop) top = Math.min(Math.max(top, minTop), maxTop);

    // Horizontal: center on the icon, then clamp so the panel stays fully on screen.
    // The panel is translated by -50%, so its edges sit at left ± panelWidth/2.
    const centerX = r.left + r.width / 2;
    const half = panelWidth / 2;
    const minLeft = margin + half;
    const maxLeft = window.innerWidth - margin - half;
    const left = minLeft > maxLeft ? window.innerWidth / 2 : Math.min(Math.max(centerX, minLeft), maxLeft);

    setCoords({ left, top, placeAbove });
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [visible, updatePosition]);

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current != null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const scheduleHideHover = () => {
    clearLeaveTimer();
    leaveTimerRef.current = setTimeout(() => {
      leaveTimerRef.current = null;
      setHovering(false);
    }, 200);
  };

  useEffect(() => () => clearLeaveTimer(), []);

  useEffect(() => {
    if (!pinned) return;
    const onDoc = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      setPinned(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPinned(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  const panelTone =
    theme === 'dark'
      ? 'border border-gray-600 bg-gray-900 text-gray-100 shadow-xl'
      : 'border border-pp-line bg-pp-surface text-pp-ink shadow-xl';

  const iconTone =
    theme === 'dark'
      ? 'text-gray-400 hover:text-white'
      : 'text-pp-muted hover:text-pp-ink';

  const tip = visible ? (
    <div
      ref={panelRef}
      role="tooltip"
      className={`pointer-events-auto fixed z-[9999] max-w-xs rounded-lg px-3 py-2 text-xs leading-relaxed ${panelTone}`}
      onMouseEnter={() => {
        clearLeaveTimer();
        setHovering(true);
      }}
      onMouseLeave={() => {
        setHovering(false);
      }}
      style={{
        left: coords.left,
        top: coords.top,
        // Never let the panel be wider than the viewport (small screens).
        maxWidth: 'min(20rem, calc(100vw - 16px))',
        transform: coords.placeAbove
          ? 'translate(-50%, -100%)'
          : 'translate(-50%, 0)',
      }}
    >
      {text}
    </div>
  ) : null;

  return (
    <>
      <span className={`relative inline-flex ${className}`}>
        <button
          ref={btnRef}
          type="button"
          className={`inline-flex cursor-help rounded-full p-0.5 outline-none ring-pp-accent focus-visible:ring-2 ${iconTone}`}
          aria-label={text}
          aria-expanded={visible}
          onMouseEnter={() => {
            clearLeaveTimer();
            setHovering(true);
          }}
          onMouseLeave={() => {
            if (!pinned) scheduleHideHover();
          }}
          onFocus={() => setHovering(true)}
          onBlur={() => setHovering(false)}
          onClick={(e) => {
            e.preventDefault();
            setPinned((p) => !p);
          }}
        >
          <HelpCircle className={iconClassName} strokeWidth={2} aria-hidden />
        </button>
      </span>
      {typeof document !== 'undefined' && tip ? createPortal(tip, document.body) : null}
    </>
  );
}
